import { useEffect, useRef } from 'react'
import { Box, Button, SimpleGrid, Table, Text } from '@chakra-ui/react';
import { Tooltip } from '@/components/ui/tooltip';
import { useNavigate } from 'react-router-dom'
import { flagLabel } from '../../types/analyticsFlags'
import type { FlagRollupRow, GeoUnitTypeKey } from '../../types/geoAnalytics'
import type { FilterGroup } from '../../types/contactList'
import { useCountScope } from '../../hooks/useAnalyticsMap'
import { extractApiMessage } from '../../lib/apiError'
import ProfileCard from './ProfileCard'
import { StatTile } from '../shared/viz'
import { uuid } from '../../lib/uuid'

interface PeopleToReachCardProps {
  flagRollups: FlagRollupRow[]
  boundaryWkt: string
  geoUnitId: string
  state: string
  unitName: string
  unitType: GeoUnitTypeKey
}

/**
 * District geographies → the VoterDistrict text field (FilterFieldRegistry) that scopes a
 * contact list to that district. The list builder has no spatial/polygon predicate, so a built
 * list is scoped by the voter's assigned district code — a close proxy for the card's polygon
 * count (they can differ slightly where a voter's district assignment and the boundary disagree).
 * Unit types with no matching VoterDistrict field (precinct, county, tract…) get no district
 * scope and fall back to a flag-only, statewide list.
 */
const DISTRICT_FIELD: Partial<Record<GeoUnitTypeKey, string>> = {
  congressionalDistrict: 'CongressionalDistrict',
  stateSenateDistrict: 'StateSenateDistrict',
  stateHouseDistrict: 'StateHouseDistrict',
}

/**
 * Flag key → FilterFieldRegistry boolean field name ("Notability Flags" fields).
 * These are the exact strings the ListBuilder preset-seeding expects; boolean
 * flags use operator Equals / value "true" (the IsUnionEmployee precedent).
 */
const FLAG_FIELD: Record<string, string> = {
  cross_district: 'FlagCrossDistrict',
  mixed_party_household: 'FlagMixedPartyHousehold',
  mega_household: 'FlagMegaHousehold',
  sleeping_giant: 'FlagSleepingGiant',
  volunteer_prospect: 'FlagVolunteerProspect',
  ballot_drop_risk: 'FlagBallotDropRisk',
  steward_reach: 'FlagStewardReach',
  new_arrival: 'FlagNewArrival',
  primary_only_leverage: 'FlagPrimaryOnlyLeverage',
}

const numberFmt = new Intl.NumberFormat('en-US')

const IMPACT_PROFILES: { profile: string; label: string }[] = [
  { profile: 'impact_gotv', label: 'Impact ≥ 80 · GOTV profile' },
  { profile: 'impact_persuasion', label: 'Impact ≥ 80 · Persuasion' },
  { profile: 'impact_primary', label: 'Impact ≥ 80 · Primary' },
]

const IMPACT_THRESHOLD = 80

function impactTip(label: string): string {
  return `${label.replace('Impact ≥ 80 · ', '')} voters whose modeled impact score is ${IMPACT_THRESHOLD} or higher inside this district — the concentration of high-leverage targets. Counts under 10 are hidden for privacy.`
}

export function PeopleToReachCard({
  flagRollups,
  boundaryWkt,
  unitName,
  unitType,
}: PeopleToReachCardProps) {
  const navigate = useNavigate()

  // One count-scope mutation per impact profile. Fire once on mount (or when the
  // polygon changes); the ref guard stops React StrictMode / re-render refiring.
  const gotv = useCountScope()
  const persuasion = useCountScope()
  const primary = useCountScope()
  const counters = [gotv, persuasion, primary]
  const countError = counters.find((c) => c.error)?.error
  const firedFor = useRef<string | null>(null)

  useEffect(() => {
    if (!boundaryWkt || firedFor.current === boundaryWkt) return
    firedFor.current = boundaryWkt
    IMPACT_PROFILES.forEach(({ profile }, i) => {
      counters[i].mutate({
        filterGroups: [],
        scope: 'highValue',
        profileNames: [profile],
        thresholds: { [profile]: IMPACT_THRESHOLD },
        polygonWkt: boundaryWkt,
      })
    })
    // counters are stable react-query mutation objects; intentionally excluded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boundaryWkt])

  const buildList = (flagKey: string) => {
    const fieldName = FLAG_FIELD[flagKey]
    if (!fieldName) return
    const filters = [
      {
        id: uuid(),
        fieldName,
        operator: 'Equals' as const,
        value: 'true',
        displayOrder: 0,
      },
    ]
    // Scope the list to this district so it matches the in-district count on the card,
    // not the statewide flag total. District number is the trailing integer of the unit
    // name ("Congressional District 6" → "6"), which matches the plain VoterDistrict codes.
    const districtField = DISTRICT_FIELD[unitType]
    const districtCode = unitName.match(/(\d+)\s*$/)?.[1]
    if (districtField && districtCode) {
      filters.push({
        id: uuid(),
        fieldName: districtField,
        operator: 'Equals' as const,
        value: districtCode,
        displayOrder: 1,
      })
    }
    const presetFilters: FilterGroup[] = [{ groupIndex: 0, filters }]
    navigate('/dashboard/lists/new', {
      state: { presetFilters, presetName: `${flagLabel(flagKey)} — ${unitName}` },
    })
  }

  return (
    <ProfileCard
      eyebrow="People to reach"
      title={`Notability flags in ${unitName}`}
      source="Counts under 10 are hidden for privacy · derived values only, no individual scores shown"
    >
      <Box overflowX="auto" mt={2}>
        <Table.Root size="sm" variant="line">
          <Table.Header bg="gray.50">
            <Table.Row>
              <Table.ColumnHeader>Flag</Table.ColumnHeader>
              <Table.ColumnHeader textAlign='end'>People</Table.ColumnHeader>
              <Table.ColumnHeader aria-label="Build list action" />
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {flagRollups.map((row) => (
              <Table.Row key={row.flagKey}>
                <Table.Cell>{flagLabel(row.flagKey)}</Table.Cell>
                <Table.Cell color={row.suppressed ? 'gray.400' : undefined} textAlign='end'>
                  {row.suppressed || row.count == null
                    ? 'insufficient data'
                    : numberFmt.format(row.count)}
                </Table.Cell>
                <Table.Cell textAlign='end'>
                  {!row.suppressed && FLAG_FIELD[row.flagKey] && (
                    <Button
                      variant='link'
                      size="sm"
                      colorPalette="brand"
                      onClick={() => buildList(row.flagKey)}
                    >
                      Build list →
                    </Button>
                  )}
                </Table.Cell>
              </Table.Row>
            ))}
            {flagRollups.length === 0 && (
              <Table.Row>
                <Table.Cell colSpan={3} color="gray.500">
                  No notability flags computed for this geography.
                </Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
        </Table.Root>
      </Box>

      <SimpleGrid columns={{ base: 1, sm: 3 }} gap={4} mt={5}>
        {IMPACT_PROFILES.map(({ profile, label }, i) => {
          const count = counters[i].data?.count
          return (
            <StatTile
              key={profile}
              label={label}
              value={
                <Tooltip content={impactTip(label)} showArrow>
                  <Text as="span" cursor="help">
                    {count != null ? numberFmt.format(count) : '—'}
                  </Text>
                </Tooltip>
              }
            />
          );
        })}
      </SimpleGrid>
      {!!countError && (
        <Text fontSize="xs" color="danger.fg" mt={2}>
          {extractApiMessage(countError, 'Could not count high-impact voters for this district.')}
        </Text>
      )}
    </ProfileCard>
  );
}
